"use client";

import { useClient, useQuery } from "@opensea/graphql";
import { useItemsWithSkeletons, useMountEffect, useOnChange } from "@opensea/ui-kit/hooks";
import { useMemo, useRef, useState } from "react";
import { usePaginatedItems } from "@/lib/pagination/use-paginated-items";
import { useScrollToTop } from "../use-scroll-to-top";
export function usePaginatedQuery(args, mapper, {
  pageSize,
  paginationPageSize = pageSize,
  onPaginationCompleted: paramOnPaginationCompleted,
  onDataLoaded,
  throwOnError,
  autoScrollToTop
}) {
  const client = useClient();
  const [{
    data,
    error,
    fetching,
    stale
  }, refetch] = useQuery(args, {
    throwOnError
  });
  const initialItems = useMemo(() => mapper(data), [data, mapper]);
  const [page, setPage] = useState(1);
  const scrollToTop = useScrollToTop();
  useMountEffect(() => {
    onDataLoaded?.();
    if (autoScrollToTop) {
      scrollToTop();
    }
  });
  useOnChange(JSON.stringify(args.variables), () => {
    if (autoScrollToTop) {
      scrollToTop();
    }
  });
  const variables = useRef(args.variables);
  variables.current = args.variables;
  const mapperFn = useRef(mapper);
  mapperFn.current = mapper;
  async function loadMore(nextPageCursor) {
    const {
      data: moreData,
      error: moreError
    } = await client.query(args.query, {
      ...variables.current,
      cursor: nextPageCursor,
      limit: paginationPageSize
    });
    if (moreError) {
      throw moreError;
    }
    return mapperFn.current(moreData);
  }
  function onPaginationCompleted(startTime, duration) {
    paramOnPaginationCompleted?.(startTime, duration);
    setPage(p => p + 1);
  }
  const [paginatedItems, setItems, pagination] = usePaginatedItems(initialItems, loadMore, {
    onPaginationCompleted
  });
  const skeletonsPageSize = page <= 1 ? pageSize : paginationPageSize;
  const items = useItemsWithSkeletons({
    items: paginatedItems,
    pageSize: skeletonsPageSize,
    isLoadingNext: pagination.isLoadingNext
  });
  const noResults = items.length === 0 && !pagination.isLoadingNext && !pagination.hasNext && !fetching && !stale;
  return {
    items,
    paginatedItems,
    setItems,
    pagination,
    error,
    noResults,
    refetch,
    fetching,
    stale,
    data
  };
}